import { syncAuthTokenToExternalExtension } from 'src/data/infinity';
import { APPLICATION_MODE, getApplicationMode } from 'src/data/system';
import * as storage from 'src/lib/storage';
import { getQueryParamValue } from 'src/lib/browser';

const TOKEN_FIELD = 'postMaloneAccessToken';

export const SOCIAL_GENERATOR_TOKEN_FIELD = 'postMaloneSocialAccessToken';

const QUERY_PARAMS = {
  authToken: 'auth_token',
  redirectTo: 'redirect_to',
  customerId: 'customer_id',
};

const Authentication = {
  /**
   * Save TOKEN in browser storage. Use 'session' storage if user do not want to save logged-in status between sessions.
   * Remove token in unused browser storage.
   *
   * @param token
   * @param sessionOnly
   */
  signIn(token, sessionOnly = false) {
    if (sessionOnly) {
      storage.removeItem(TOKEN_FIELD);
      storage.writeItem(TOKEN_FIELD, token, { session: true });
    } else {
      storage.removeItem(TOKEN_FIELD, { session: true });
      storage.writeItem(TOKEN_FIELD, token);
    }

    this.notifyExtension();
  },

  signOut() {
    storage.removeItem(TOKEN_FIELD, { session: true });
    storage.removeItem(TOKEN_FIELD);

    this.notifyExtension();
  },

  getQueryToken() {
    return getQueryParamValue(QUERY_PARAMS.authToken);
  },

  getQueryCustomerId() {
    return getQueryParamValue(QUERY_PARAMS.customerId);
  },

  getQueryRedirectTo() {
    return getQueryParamValue(QUERY_PARAMS.redirectTo);
  },

  isAuthenticated() {
    const token = this.getToken();
    return Boolean(token);
  },

  getToken() {
    const queryToken = this.getQueryToken();
    if (queryToken) {
      return queryToken;
    }

    const sessionToken = storage.readItem(TOKEN_FIELD, { session: true });
    if (sessionToken) {
      return sessionToken;
    }

    return storage.readItem(TOKEN_FIELD);
  },

  getSocialGeneratorToken() {
    const socialGeneratorSessionToken = storage.readItem(SOCIAL_GENERATOR_TOKEN_FIELD, {
      session: true,
    });
    if (socialGeneratorSessionToken) {
      return socialGeneratorSessionToken;
    }

    return storage.readItem(SOCIAL_GENERATOR_TOKEN_FIELD);
  },

  /**
   * Notify extension about authentication status change
   * Do not react to errors
   */
  notifyExtension() {
    // "embedded" Platform should not send out "external" messages
    if (getApplicationMode() === APPLICATION_MODE.infinityExtension) {
      return;
    }

    if (this.isAuthenticated()) {
      const token = this.getToken();
      syncAuthTokenToExternalExtension(token).catch(() => {
        /* do nothing */
      });
    } else {
      syncAuthTokenToExternalExtension(null).catch(() => {
        /* do nothing */
      });
    }
  },

  subscribeToTokenChanges(listener) {
    if (!listener) {
      return null;
    }

    const handler = (event) => {
      // HINT: this fixes issue in Safari - we don't react to event, when it was fired for the same tab which changed value
      if (document.hasFocus()) {
        return;
      }

      if (event.key === TOKEN_FIELD) {
        listener(event.oldValue, event.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  },
};

export default Authentication;
